import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

// Calibrated Modern: full-bordered inputs with brand-blue focus ring,
// uppercase tracked labels, generous vertical rhythm.  Touch targets
// hit 44px at default size to meet Apple/Material guidance.
const baseInputClasses = [
  'block w-full px-3.5 py-2.5 text-[15px] leading-tight min-h-[44px]',
  'bg-[var(--color-surface)] text-[var(--color-ink)] placeholder-[var(--color-ink-faint)]',
  'border border-[var(--color-rule-strong)] rounded-[4px]',
  'focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20',
  'transition-colors duration-150',
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-[var(--color-surface-muted)]',
].join(' ');

const labelClasses =
  'mb-2 block font-sans text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-ink-muted)]';

const errorClasses =
  'mt-2 text-[13px] font-medium text-[var(--color-danger)]';
const hintClasses = 'mt-2 text-[13px] text-[var(--color-ink-soft)]';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, id, className = '', type, ...rest }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    // Surface the HTML `required` attribute as a visual asterisk so the
    // user knows up-front, not after submit. Reads as "required" to
    // assistive tech via aria-label.
    const isRequired = rest.required === true;
    // When the user has clicked "show", flip the rendered type but
    // leave aria-attributes alone so screen readers continue to treat
    // the field as sensitive.
    const renderedType = isPassword && showPassword ? 'text' : type;

    const fieldClasses = [
      baseInputClasses,
      error
        ? '!border-[var(--color-danger)] focus:!border-[var(--color-danger)] focus:!ring-[var(--color-danger)]/20'
        : '',
      // Reserve room for the show/hide icon on password fields so
      // long values don't slide under it.
      isPassword ? 'pr-11' : '',
      className,
    ].join(' ');

    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={labelClasses}>
            {label}
            {isRequired && (
              <span
                aria-label="required"
                className="ml-1 text-[var(--color-danger)]"
              >
                *
              </span>
            )}
          </label>
        )}
        <div className={isPassword ? 'relative' : undefined}>
          <input
            ref={ref}
            id={inputId}
            type={renderedType}
            aria-describedby={
              error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
            }
            aria-invalid={error ? true : undefined}
            className={fieldClasses}
            {...rest}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer rounded-[3px] p-2 text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} role="alert" className={errorClasses}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className={hintClasses}>
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, id, className = '', ...rest }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const errorId = selectId ? `${selectId}-error` : undefined;
    const hintId = selectId ? `${selectId}-hint` : undefined;
    const isRequired = rest.required === true;
    return (
      <div>
        {label && (
          <label htmlFor={selectId} className={labelClasses}>
            {label}
            {isRequired && (
              <span
                aria-label="required"
                className="ml-1 text-[var(--color-danger)]"
              >
                *
              </span>
            )}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? errorId : hint ? hintId : undefined
          }
          className={[
            baseInputClasses,
            'appearance-none pr-10 bg-no-repeat cursor-pointer',
            // Custom chevron in primary blue
            'bg-[url("data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%2712%27%20height%3D%277%27%20viewBox%3D%270%200%2012%207%27%3E%3Cpath%20fill%3D%27none%27%20stroke%3D%27%232484c6%27%20stroke-width%3D%271.5%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%20d%3D%27M1%201l5%205%205-5%27%2F%3E%3C%2Fsvg%3E")]',
            'bg-[length:12px_7px] bg-[right_0.875rem_center]',
            error
              ? '!border-[var(--color-danger)] focus:!border-[var(--color-danger)] focus:!ring-[var(--color-danger)]/20'
              : '',
            className,
          ].join(' ')}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} role="alert" className={errorClasses}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className={hintClasses}>
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, id, className = '', ...rest }, ref) => {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const errorId = textareaId ? `${textareaId}-error` : undefined;
    const hintId = textareaId ? `${textareaId}-hint` : undefined;
    const isRequired = rest.required === true;
    return (
      <div>
        {label && (
          <label htmlFor={textareaId} className={labelClasses}>
            {label}
            {isRequired && (
              <span
                aria-label="required"
                className="ml-1 text-[var(--color-danger)]"
              >
                *
              </span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? errorId : hint ? hintId : undefined
          }
          className={[
            'block w-full px-3.5 py-3 text-[15px] leading-relaxed font-sans',
            'bg-[var(--color-surface)] text-[var(--color-ink)] placeholder-[var(--color-ink-faint)]',
            'border border-[var(--color-rule-strong)] rounded-[4px]',
            'focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20',
            'transition-colors duration-150',
            'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-[var(--color-surface-muted)]',
            'min-h-[112px] resize-y',
            error
              ? '!border-[var(--color-danger)] focus:!border-[var(--color-danger)] focus:!ring-[var(--color-danger)]/20'
              : '',
            className,
          ].join(' ')}
          {...rest}
        />
        {error && (
          <p id={errorId} role="alert" className={errorClasses}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className={hintClasses}>
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';


// ─────────────────────────────────────────────────────────────────────
// Eye icons for the password show/hide toggle. Inline SVGs (no icon
// library dep) — Heroicons outline-style at 18×18.
// ─────────────────────────────────────────────────────────────────────

function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24" />
      <path d="M9.88 5.08A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a17.9 17.9 0 0 1-3.06 4.06" />
      <path d="M6.61 6.61A17.91 17.91 0 0 0 2 12s3.5 7 10 7a10.94 10.94 0 0 0 5.39-1.46" />
    </svg>
  );
}
